import axios from "axios";
import { isSsoHandoffLocked, isSsoRoute } from "../utils/sso-entry";
import {
  clearAuthTokens,
  getAccessToken,
  getApiRootUrl,
  getRefreshToken,
  setTokenPair,
} from "./token.service";

const authClient = axios.create({
  timeout: 15000,
});

const authUrl = (path) => `${getApiRootUrl()}${path}`;

const shouldBlockServerRevokeForHandoff = () => {
  if (!isSsoHandoffLocked()) {
    return false;
  }

  if (typeof window === "undefined") {
    return true;
  }

  // The /sso tab performing handoff must still revoke stale sessions locally.
  return !isSsoRoute(window.location.pathname || "");
};
const refreshCsrfCookieName = process.env.VUE_APP_AUTH_REFRESH_CSRF_COOKIE_NAME || "pg_refresh_csrf";
const refreshCsrfHeaderName = process.env.VUE_APP_AUTH_REFRESH_CSRF_HEADER || "X-Refresh-CSRF";

const getCookieValue = (name) => {
  if (typeof document === "undefined") {
    return "";
  }

  const cookies = document.cookie ? document.cookie.split(";") : [];
  for (const cookie of cookies) {
    const [rawKey, ...rawValue] = cookie.trim().split("=");
    if (rawKey === name) {
      return decodeURIComponent(rawValue.join("="));
    }
  }
  return "";
};

const normalizeTokenResponse = (data = {}) => {
  const accessToken = typeof data.access_token === "string" ? data.access_token : null;
  const refreshToken = typeof data.refresh_token === "string" ? data.refresh_token : null;
  return { accessToken, refreshToken, raw: data };
};

export const establishRefreshSession = async ({
  accessToken = getAccessToken(),
  deviceName = "vue-web",
} = {}) => {
  if (!accessToken) {
    throw new Error("Cannot establish refresh session without an access token.");
  }

  const response = await authClient.post(
    authUrl("/auth/token/issue"),
    {
      use_cookie: true,
      device_name: deviceName,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }
  );

  const normalized = normalizeTokenResponse(response.data);
  setTokenPair({
    accessToken: normalized.accessToken || accessToken,
    refreshToken: normalized.refreshToken !== null ? normalized.refreshToken : undefined,
  });

  return normalized.raw;
};

export const refreshAccessSession = async ({
  deviceName = "vue-web",
} = {}) => {
  const legacyRefreshToken = getRefreshToken();
  const accessToken = getAccessToken();

  const headers = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const csrfToken = getCookieValue(refreshCsrfCookieName);
  if (csrfToken) {
    headers[refreshCsrfHeaderName] = csrfToken;
  }

  const payload = {
    device_name: deviceName,
  };

  // Legacy-compatibility fallback: support old refresh-token based sessions.
  if (legacyRefreshToken) {
    payload.refresh_token = legacyRefreshToken;
  }

  let response;
  try {
    response = await authClient.post(authUrl("/auth/token/refresh"), payload, {
      headers,
      withCredentials: true,
    });
  } catch (error) {
    const status = error?.response?.status;

    // Self-heal stale/legacy cookie states so users are never forced
    // to manually clear browser cookies after auth-flow changes.
    if (status === 401 || status === 403) {
      try {
        await authClient.post(
          authUrl("/auth/token/logout"),
          {},
          {
            headers,
            withCredentials: true,
          }
        );
      } catch (logoutError) {
        // Ignore cleanup failures; we still clear local auth state below.
      } finally {
        clearAuthTokens();
      }
    }

    throw error;
  }

  const normalized = normalizeTokenResponse(response.data);
  if (!normalized.accessToken) {
    throw new Error("Refresh endpoint did not return an access token.");
  }

  setTokenPair({
    accessToken: normalized.accessToken,
    refreshToken: normalized.refreshToken !== null ? normalized.refreshToken : undefined,
  });

  return normalized.raw;
};

export const revokeAuthSession = async ({
  allDevices = false,
} = {}) => {
  if (shouldBlockServerRevokeForHandoff()) {
    clearAuthTokens();
    return;
  }

  const legacyRefreshToken = getRefreshToken();
  const accessToken = getAccessToken();

  const headers = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const csrfToken = getCookieValue(refreshCsrfCookieName);
  if (csrfToken) {
    headers[refreshCsrfHeaderName] = csrfToken;
  }

  try {
    await authClient.post(
      authUrl("/auth/token/logout"),
      {
        all_devices: !!allDevices,
        refresh_token: legacyRefreshToken || undefined,
      },
      {
        headers,
        withCredentials: true,
      }
    );
  } finally {
    clearAuthTokens();
  }
};

/**
 * Clear in-memory tokens and httpOnly refresh cookies when loading a public
 * auth page (password reset, login, etc.). Prevents a normal browser tab from
 * silently re-authenticating via pg_refresh while the user resets a password.
 */
export const clearLocalAuthSession = () => {
  clearAuthTokens();
};

export const purgeAuthSessionForPublicAuthPage = async ({
  allowServerRevoke = true,
} = {}) => {
  clearAuthTokens();

  if (!allowServerRevoke || shouldBlockServerRevokeForHandoff()) {
    return;
  }

  try {
    await revokeAuthSession();
  } catch (error) {
    // Logout may fail without a bearer token; cookies are still cleared locally.
    clearAuthTokens();
    console.warn("[auth-session] Public auth page session purge completed with logout error", error);
  }
};

export const bootstrapRefreshSession = async () => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (accessToken) {
    if (!refreshToken) {
      try {
        return await establishRefreshSession({ accessToken });
      } catch (error) {
        console.warn(
          "[auth-session] Bootstrap cookie issuance failed; continuing with in-memory access token",
          error
        );
      }
    }

    return null;
  }

  try {
    return await refreshAccessSession();
  } catch (error) {
    // User might be logged out; swallow boot refresh failures.
    console.warn("[auth-session] Bootstrap refresh-session restore failed", error);
    return null;
  }
};
